import { events, type EventsChannel } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useRef, useState } from 'react';

import {
  TDocument,
  TUseAppsyncEventsParams,
} from '@/shared/hooks/use-appsync-events/use-app-sync-events.types';

export const useAppSyncEvents = ({
  webSocketChannel = '',
  connectOnInit = false,
  onMessageReceived,
}: TUseAppsyncEventsParams) => {
  const channel = useRef<EventsChannel>(null);
  const sub = useRef<ReturnType<EventsChannel['subscribe']>>(null);
  const [error, setError] = useState<boolean>(false);

  const connectAndSubscribe = async (
    webSocketChannel: string,
    onMessageReceived?: (value: any) => void,
    onError?: () => void,
  ): Promise<boolean> => {
    try {
      const session = await fetchAuthSession();
      const authToken = session?.tokens?.idToken?.toString() || '';

      console.debug(`Subscribing to ${webSocketChannel}`);
      channel.current = await events.connect(webSocketChannel, {
        authToken,
      });

      if (!sub.current) {
        sub.current = channel.current.subscribe({
          next: (data) => {
            if (!data) {
              return;
            }

            console.log('message received', { data });
            console.log('on channel', { webSocketChannel });
            onMessageReceived?.(data);
          },
          error: (err) => {
            console.error('error', err);
            setError(true);
            onError?.();
          },
        });
      }
      return true;
    } catch (error) {
      // Without this, a failed connect resolves silently and the caller
      // proceeds as if subscribed, waiting for events that can never arrive.
      console.error(error);
      setError(true);
      onError?.();
      return false;
    }
  };

  const postMessage = async (message: TDocument) => {
    try {
      console.debug(`Posting message via HTTP to ${webSocketChannel}`);
      await events.post(webSocketChannel, message);
    } catch (error) {
      console.error(error);
    }
  };

  // Returns whether the message actually went out -- failures are swallowed
  // here, so callers that report to the user have no other signal.
  const publishMessage = async (message: TDocument): Promise<boolean> => {
    if (!channel.current) {
      console.debug('Channel is undefined');
      return false;
    }

    try {
      console.debug(`Publishing message to ${webSocketChannel}`);
      await channel.current.publish(message);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const disconnect = () => {
    sub.current?.unsubscribe();
    sub.current = null;
    // Unsubscribing leaves the channel in Amplify's open-channel set, so the
    // shared WebSocket is never torn down. Only close() releases it.
    channel.current?.close();
    channel.current = null;
    setError(false);
  };

  useEffect(() => {
    if (!connectOnInit) {
      return;
    }

    void connectAndSubscribe(webSocketChannel, onMessageReceived);

    return disconnect;
  }, []);

  return {
    postMessage,
    publishMessage,
    connectAndSubscribe,
    disconnect,
    current: sub?.current,
    error,
  };
};
